/**
 * 解析後端時間字串成 ms timestamp。
 *
 * 接受三種形式，都是後端可能給出的合法表示，語意一律是同一個絕對時間點：
 * - `2026-05-12 08:16:22`          REST API 的格式（空格分隔、無時區標記，語意為 UTC）
 * - `2026-05-12T08:16:22.000000Z`  ISO 8601 UTC，Laravel 廣播事件（WebSocket）序列化 Carbon 的預設輸出
 * - `2026-05-12T16:16:22+08:00`    ISO 8601 帶時區位移
 *
 * 為什麼不直接用 `new Date(str)`：
 * - 第一種是空格分隔、無時區標記的非標準字串。Chrome(V8) 容忍，但 Safari(JSC) 會把它當
 *   「本地時間」解析，時區直接差 8 小時；加 ` UTC` 後綴則回傳 Invalid Date(NaN)。
 * - 用 regex 拆解 + Date.UTC() 明確計算，跨瀏覽器行為完全一致。
 *
 * 實際狀況：REST API 與 WebSocket 廣播目前都是第一種格式（前者對照 Postman 文件逐欄確認，
 * 後者實際抓 MessageSent／BellNotificationSent 封包確認）。ISO 那兩個分支是防禦性的，
 * 不是為了現有的任何 producer——Laravel 序列化 Carbon 的預設輸出就是 ISO 8601，
 * 後端哪天改用預設序列化時，這裡會直接算對而不是默默錯 8 小時。
 *
 * 結尾錨點則不論如何都必要：舊版沒有錨點，會**默默忽略**後綴——遇到 `Z` 剛好是對的
 * （本來就當 UTC 算），遇到 `+08:00` 就少算 8 小時且無聲無息。現在沒見過的格式一律回 null，
 * 由上層 console.error 出聲，不會給出一個看起來很合理的錯誤時間。
 *
 * @param {string} str 後端時間字串
 * @returns {number|null} ms timestamp，無法解析時回傳 null
 */
export function parseBackendDate(str) {
  if (!str) return null;

  const m = str.trim().match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/
  );
  if (!m) return null;

  const ts = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
  if (!Number.isFinite(ts)) return null;

  const zone = m[7];
  // 無標記或 Z：字串上的數字本身就是 UTC，Date.UTC 算出來的就是答案
  if (!zone || zone === 'Z') return ts;

  // 帶位移：字串上的數字是該時區的牆上時間，減掉位移才是 UTC
  const sign = zone[0] === '-' ? -1 : 1;
  const hours = +zone.slice(1, 3);
  const minutes = +zone.slice(-2);
  return ts - sign * (hours * 60 + minutes) * 60_000;
}
